/**
 * Display vocabulary for the provisional-closure lifecycle.
 *
 * The status itself and both deadlines are computed by the backend and arrive as `dockLifecycle` on
 * every dock trip projection, so no deadline arithmetic is repeated here. This file only maps that
 * status to words, a badge class and a tooltip — once, so the board, the operations table and the
 * trip drawer cannot describe the same trip differently.
 *
 * A provisional trip is a labelled business status, never a Gate Out: nothing here renders an exit
 * movement, an exit icon, or a completed count. The trip's own `trip_over` is the only thing that
 * says a trip finished, and it is passed through untouched.
 */

// Short chip text. The sentence an operator reads lives in lifecycleSummary below, so a badge and
// the line beside it never repeat each other word for word.
export const DOCK_LIFECYCLE_LABELS = {
	WAITING_SECOND_BAY: "Awaiting second bay",
	PROVISIONAL: "Awaiting return",
	FINALIZATION_PENDING: "Closing",
	ACTIVE_SECOND_BAY: "At second bay",
	COMPLETED: "Completed",
};

const BADGE_CLASSES = {
	WAITING_SECOND_BAY: "dock-status-reserved",
	PROVISIONAL: "dock-status-provisional",
	FINALIZATION_PENDING: "dock-status-terminated",
	ACTIVE_SECOND_BAY: "dock-status-ongoing",
	COMPLETED: "dock-status-completed",
};

// The statuses the provisional panel owns. A returned trip is back on its Bay card; a completed one
// is history. Neither belongs in a list of trips this feature is still holding.
export const DOCK_PENDING_STATUSES = ["WAITING_SECOND_BAY", "PROVISIONAL", "FINALIZATION_PENDING"];

export function formatPlantTime(value, timezone) {
	if (!value) return "—";
	return new Intl.DateTimeFormat("en-MY", {
		timeZone: timezone, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
	}).format(new Date(value));
}

export function lifecycleBadgeClass(lifecycle) {
	return BADGE_CLASSES[lifecycle?.status] || "dock-status-vacant";
}

/**
 * The one-line status an operator reads.
 *
 * A provisional trip says until when a return is still allowed, because that is the decision in front
 * of the operator. Once the window has ended it says so instead — never a countdown to a permission
 * that no longer exists — and says review when the closure is actually stuck.
 */
export function lifecycleSummary(lifecycle, timezone) {
	if (!lifecycle?.status) return null;
	switch (lifecycle.status) {
		case "WAITING_SECOND_BAY":
			return `Return allowed until ${formatPlantTime(lifecycle.returnUntil, timezone)}`;
		case "PROVISIONAL":
			return `Trip ongoing · return allowed until ${formatPlantTime(lifecycle.returnUntil, timezone)}`;
		case "FINALIZATION_PENDING":
			return lifecycle.reviewRequired ? "Closure needs review" : "Return window ended · closing";
		case "ACTIVE_SECOND_BAY":
			return DOCK_LIFECYCLE_LABELS.ACTIVE_SECOND_BAY;
		default:
			return DOCK_LIFECYCLE_LABELS[lifecycle.status] || null;
	}
}

export function lifecycleBadgeLabel(lifecycle) {
	if (!lifecycle?.status) return null;
	if (lifecycle.status === "FINALIZATION_PENDING" && lifecycle.reviewRequired) return "Needs review";
	return DOCK_LIFECYCLE_LABELS[lifecycle.status] || null;
}
